<template>
    <v-container class="pa-2">
        <v-row dense>
            <v-col cols="12">

                <!-- Header sticky -->
                <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3 sticky-header">
                    <div class="d-flex align-center">
                        <v-btn v-if="effectiveIdCongregazione && !isInline" icon small text color="primary" @click="chiudi">
                            <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <div class="ml-1 flex-grow-1">
                            <div class="text-subtitle-2 font-weight-bold primary--text text-center">
                                {{ effectiveIdCongregazione ? 'Oratori ' + effectiveNome : 'Oratori Esterni ' + nomeCircoscrizione }}
                            </div>
                        </div>
                        <v-btn v-if="(isAdmin || $store.state.oratore.isAssistente) && !effectiveIdCongregazione"
                               icon :loading="loadingCsvOratori"
                               @click="dialogNws = true">
                            <img src="@/assets/nws_logo.webp" width="18" height="18" style="border-radius:4px;background:linear-gradient(rgb(50,143,196),rgb(78,197,212));padding:2px" />
                        </v-btn>
                        <v-btn v-if="puoAggiungereOratore && effectiveIdCongregazione"
                               icon color="primary"
                               @click="setDetailOratore && $vuetify.breakpoint.mdAndUp ? setDetailOratore('0', effectiveIdCongregazione) : $router.push({ path: '/oratore/0', query: { idCongregazione: effectiveIdCongregazione } })">
                            <v-icon>mdi-account-plus</v-icon>
                        </v-btn>
                    </div>
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="mdi-magnify"
                        :placeholder="effectiveIdCongregazione ? 'Cerca oratore' : 'Cerca oratore, congregazione...'"
                        outlined rounded dense hide-details clearable
                        class="mt-2"
                    />
                    <div v-if="effectiveIdCongregazione && puoVedereNascosti"
                         class="d-flex align-center mt-2">
                        <v-chip x-small
                                :color="mostraNascosti ? 'error' : ($vuetify.theme.dark ? 'grey darken-2' : 'grey lighten-2')"
                                :dark="mostraNascosti || $vuetify.theme.dark"
                                class="cursor-pointer"
                                @click="toggleNascosti">
                            <v-icon x-small left>{{ mostraNascosti ? 'mdi-eye' : 'mdi-eye-off-outline' }}</v-icon>
                            {{ mostraNascosti ? 'Solo oratori attivi' : 'Mostra oratori nascosti' }}
                        </v-chip>
                    </div>
                </v-card>

                <!-- Skeleton -->
                <template v-if="loadingOratori">
                    <v-card v-for="n in 5" :key="n" class="rounded-xl elevation-1 mb-2 px-3 py-2">
                        <v-skeleton-loader type="list-item-avatar-two-line" />
                    </v-card>
                </template>

                <!-- Lista -->
                <template v-else>
                    <v-card v-for="item in oratoriFiltrati" :key="item.oratore.id"
                            class="rounded-xl elevation-2 mb-2 oratore-card"
                            :class="[isOratoreCliccabile(item) ? 'card-cliccabile' : '', item.oratore.isEliminato ? 'card-nascosta' : '']"
                            @click="clickOratore(item)">
                        <div class="d-flex align-center px-3 py-2">
                            <!-- Avatar -->
                            <v-avatar size="40" color="primary" class="mr-3 flex-shrink-0">
                                <span class="white--text" style="font-size:0.65rem;font-weight:700">
                                    {{ initiali(item.oratore.nome, item.oratore.cognome) }}
                                </span>
                            </v-avatar>

                            <!-- Contenuto -->
                            <div class="flex-grow-1" style="min-width:0">
                                <div class="d-flex align-center flex-wrap" style="gap:3px">
                                    <span class="text-body-2 font-weight-bold"
                                          :class="item.oratore.isEliminato ? 'text-decoration-line-through grey--text' : ''">
                                        {{ item.oratore.nome }} {{ item.oratore.cognome }}
                                    </span>
                                    <v-chip v-if="item.oratore.isEliminato" x-small dark color="error" class="ml-1">nascosto</v-chip>
                                    <v-chip v-if="item.oratore.isAnziano"      x-small dark color="teal" class="ml-1">A</v-chip>
                                    <v-chip v-if="item.oratore.isSM"           x-small dark color="blue darken-1" class="ml-1">SM</v-chip>
                                    <v-chip v-if="item.oratore.isAssistente"   x-small dark color="grey darken-2" class="ml-1">ASSISTENTE</v-chip>
                                    <v-chip v-if="item.oratore.isCoordinatore" x-small dark color="deep-orange darken-4" class="ml-1">ADM</v-chip>
                                    <v-chip v-if="item.oratore.isAdmin"        x-small dark color="orange darken-1" class="ml-1">APP-ADM</v-chip>
                                    <v-chip v-if="item.oratore.isInterno"      x-small dark color="purple darken-1" class="ml-1">INTERNO</v-chip>
                                    <chip-login-oratore :oratore="item.oratore" class="ml-1" />
                                </div>
                                <div class="text-caption grey--text">{{ item.oratore.congregazione.nome }}</div>
                                <div class="d-flex align-center flex-wrap mt-1" style="gap:10px">
                                    <span v-if="item.oratore.telefono"
                                          class="text-caption grey--text text--darken-1 contact-link"
                                          @click.stop="openTel(item.oratore.telefono)">
                                        <v-icon x-small color="primary" class="mr-1">mdi-phone-outline</v-icon>{{ item.oratore.telefono }}
                                    </span>
                                    <span v-if="item.oratore.mail"
                                          class="text-caption grey--text text--darken-1 contact-link"
                                          @click.stop="openMail(item.oratore.mail)">
                                        <v-icon x-small color="teal" class="mr-1">mdi-email-outline</v-icon>{{ item.oratore.mail }}
                                    </span>
                                </div>
                            </div>

                            <!-- Destra -->
                            <div class="d-flex flex-column align-end ml-2 flex-shrink-0">
                                <span v-if="item.numeroSchemi > 0" class="schema-badge mb-1">
                                    {{ item.numeroSchemi }} schem{{ item.numeroSchemi > 1 ? 'i' : 'a' }}
                                </span>
                                <v-icon v-if="isOratoreCliccabile(item)" color="primary" size="18">mdi-chevron-right</v-icon>
                                <v-icon v-else color="grey lighten-2" size="18">mdi-minus</v-icon>
                            </div>
                        </div>
                    </v-card>

                    <div v-if="oratoriFiltrati.length === 0" class="text-center grey--text py-10">
                        <v-icon large color="grey lighten-1">mdi-account-off-outline</v-icon>
                        <div class="mt-2 text-body-2">Nessun oratore trovato</div>
                    </div>
                </template>

            </v-col>
        </v-row>

        <!-- Dialog NWS Export -->
        <v-dialog v-model="dialogNws" max-width="440">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center" style="gap:8px">
                    <img src="@/assets/nws_logo.webp" width="22" height="22" style="border-radius:5px;background:linear-gradient(rgb(50,143,196),rgb(78,197,212));padding:2px" />
                    Esportazione per NW Scheduler
                    <v-chip x-small color="orange" dark class="ml-1" style="font-size:10px">BETA</v-chip>
                </v-card-title>
                <v-card-text class="text-body-2">
                    <div class="mb-2">Verranno prodotti file CSV da caricare in NW Scheduler.</div>
                    <div class="mb-1 font-weight-medium">Si consiglia di esportare e caricare nell'ordine:</div>
                    <ol class="pl-4 mb-3" style="line-height:1.8">
                        <li><strong>Congregazioni:</strong> unico file CSV</li>
                        <li class="primary--text font-weight-bold">Oratori: file ZIP con un CSV per ogni congregazione <span class="grey--text font-weight-regular">(il sw non consente un caricamento massivo)</span> &nbsp;<v-chip x-small color="primary" dark>questa esportazione</v-chip></li>
                        <li><strong>Programma Interno:</strong> unico file CSV. Su NW Scheduler indicare <code>dd/mm/yyyy</code> come formato data</li>
                    </ol>
                    <v-alert dense text type="info" class="text-caption mb-0">
                        Si consiglia di fare alcune prove su NW Scheduler con porzioni dei file generati.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogNws = false">Annulla</v-btn>
                    <v-btn color="primary" depressed @click="dialogNws = false; esportaCsvOratori()">
                        <v-icon left small>mdi-download</v-icon>Esporta
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import { callService, callPost, euro, gLocalData, gCheckData, gServerData, gDataCalendar, Snack, SUPERADMIN_CODE } from '@/modules/utilities.js'
import { bus } from '@/main.js'
import ChipLoginOratore from '@/components/ChipLoginOratore.vue'

export default {
    components: { ChipLoginOratore },
    inject: {
        setDetailOratore: { default: null },
    },
    props: {
        inlineIdCongregazione: { type: [String, Number], default: null },
        inlineNome: { type: String, default: null },
    },
    data: () => ({
        oratoriEstesi: [],
        oratore: {},
        discorsi: [],
        listaDiscorsi: [],
        loadingOratori: false,
        loadingCsvOratori: false,
        dialogNws: false,
        SUPERADMIN_CODE: '',
        isAdmin: false,
        search: '',
        mostraNascosti: false,
        _lastFetchedId: undefined,
    }),
    computed: {
        isInline() {
            return this.inlineIdCongregazione !== null;
        },
        effectiveIdCongregazione() {
            return this.inlineIdCongregazione != null
                ? String(this.inlineIdCongregazione)
                : this.$route.params.idCongregazione;
        },
        effectiveNome() {
            return this.inlineNome != null
                ? this.inlineNome
                : this.$route.params.nome;
        },
        nomeCircoscrizione() {
            return this.$store.state.circoscrizione?.nome || '';
        },
        puoVedereNascosti() {
            const o = this.$store.state.oratore;
            if (o.codice == this.SUPERADMIN_CODE || o.isAdmin) return true;
            return (o.isCoordinatore || o.isAssistente) &&
                   o.congregazione &&
                   o.congregazione.id == this.effectiveIdCongregazione;
        },
        puoAggiungereOratore() {
            const o = this.$store.state.oratore;
            if (o.codice == this.SUPERADMIN_CODE || o.isAdmin) return true;
            return (o.isCoordinatore || o.isAssistente) &&
                   o.congregazione &&
                   o.congregazione.id == this.effectiveIdCongregazione;
        },
        isSuperAdmin() {
            return this.$store.state.oratore.codice == this.SUPERADMIN_CODE;
        },
        puoVedereInterni() {
            const o = this.$store.state.oratore;
            return o.codice == this.SUPERADMIN_CODE || o.isAdmin || o.isCoordinatore || o.isAssistente;
        },
        oratoriFiltrati() {
            if (!Array.isArray(this.oratoriEstesi)) return [];
            if (!this.search) return this.oratoriEstesi;
            const s = this.search.toLowerCase();
            return this.oratoriEstesi.filter(item => {
                if (!item || !item.oratore) return false;
                const o = item.oratore;
                return (
                    o.nome.toLowerCase().includes(s) ||
                    o.cognome.toLowerCase().includes(s) ||
                    (o.congregazione.nome && o.congregazione.nome.toLowerCase().includes(s)) ||
                    (o.telefono && o.telefono.includes(s)) ||
                    (o.mail && o.mail.toLowerCase().includes(s))
                );
            });
        }
    },
    methods: {
        initiali(nome, cognome) {
            const n = nome ? nome[0] : '';
            const c = cognome ? cognome[0] : '';
            return (n + c).toUpperCase();
        },
        isOratoreCliccabile(oratoreEsteso) {
            const o = this.$store.state.oratore;
            const privilegiato = o.codice == this.SUPERADMIN_CODE || o.isAdmin || o.isCoordinatore || o.isAssistente;
            return privilegiato || o.id == oratoreEsteso.oratore.id;
        },
        async clickOratore(oratoreEsteso) {
            if (!this.isOratoreCliccabile(oratoreEsteso)) return;
            const isTablet = this.$vuetify.breakpoint.mdAndUp;
            if (this.setDetailOratore && (this.isInline || isTablet)) {
                this.setDetailOratore(oratoreEsteso.oratore.id);
            } else {
                this.$router.push({ name: "oratore", params: { id: oratoreEsteso.oratore.id } });
            }
        },
        async apriOratore(item) {
            this.$router.push({ name: "oratore", params: { id: item.oratore.id } });
        },
        async onUpdateOratore({ idOra, idCong }) {
            const i = this.oratoriEstesi.findIndex(o => o.oratore.id == idOra);
            if (i === -1) {
                // Oratore nuovo: non è ancora in lista.
                // Potremmo essere ancora sulla route /oratore, quindi ricaricaSilente
                // abortisce (effectiveIdCongregazione è undefined). Usiamo un flag
                // che viene letto in beforeRouteEnter quando l'utente torna qui.
                this._needsRefresh = true;
                return;
            }
            if (i !== -1) {
                try {
                    let qs = [idCong ? `idCong=${idCong}` : '', idOra ? `idOra=${idOra}` : ''].filter(Boolean).join('&');
                    if (this.mostraNascosti && idCong) qs += (qs ? '&' : '') + 'mostraNascosti=true';
                    const oraExt = await callService(`/api/oratore/congregazioneOratore${qs ? '?' + qs : ''}`, [this.$store.state.idCircoscrizione, !!idCong && this.puoVedereInterni]);
                    if (oraExt && oraExt.length > 0) {
                        this.oratoriEstesi.splice(i, 1, oraExt[0]);
                    } else {
                        this.oratoriEstesi.splice(i, 1);
                    }
                } catch (error) {
                    console.error("Errore nell'aggiornamento dell'oratore:", error);
                    this.oratoriEstesi.splice(i, 1);
                }
            }
        },
        async esportaCsvOratori() {
            this.loadingCsvOratori = true;
            const out = await callService('/api/oratore/exportOratori', [this.$store.state.idCircoscrizione]);
            if (out && out.fileOutput) {
                const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + out.fileOutput;
                const filename = out.fileOutput.split('/').pop();
                if (window.HybridWebView) {
                    this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
                } else {
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.target = '_blank';
                    a.click();
                }
            }
            this.loadingCsvOratori = false;
        },
        openTel(tel) {
            if (!tel) return;
            this.$openUrl('tel:' + tel.replace(/\s/g, ''));
        },
        openMail(mail) {
            if (!mail) return;
            this.$openUrl('mailto:' + mail);
        },
        chiudi() {
            if (this.isInline) {
                this.$emit('close');
            } else {
                this.$router.back();
            }
        },
        async toggleNascosti() {
            this.mostraNascosti = !this.mostraNascosti;
            await this.fetchOratori(this.effectiveIdCongregazione);
        },
        async ricaricaSilente() {
            try {
                const idCong = this.effectiveIdCongregazione;
                // Se durante una transizione di navigazione effectiveIdCongregazione è momentaneamente
                // null ma _lastFetchedId è valorizzato, saltiamo per evitare di caricare tutti gli oratori
                if (!idCong && this._lastFetchedId) return;
                let qs = idCong ? `?idCong=${idCong}` : '';
                if (this.mostraNascosti && idCong) qs += '&mostraNascosti=true';
                const responseData = await callService(`/api/oratore/congregazioneOratore${qs}`, [this.$store.state.idCircoscrizione, !!idCong && this.puoVedereInterni]);
                if (Array.isArray(responseData)) {
                    this.oratoriEstesi = responseData.filter(item => item && item.oratore);
                }
            } catch (error) {
                console.error("Errore ricaricamento silente oratori:", error);
            }
        },
        async fetchOratori(idCongregazione) {
            this.loadingOratori = true;
            this.oratoriEstesi = [];
            try {
                let qs = idCongregazione ? `?idCong=${idCongregazione}` : '';
                if (this.mostraNascosti && idCongregazione) qs += '&mostraNascosti=true';
                const responseData = await callService(`/api/oratore/congregazioneOratore${qs}`, [this.$store.state.idCircoscrizione, !!idCongregazione && this.puoVedereInterni]);
                if (Array.isArray(responseData)) {
                    this.oratoriEstesi = responseData.filter(item => item && item.oratore);
                } else {
                    this.oratoriEstesi = [];
                }
            } catch (error) {
                console.error("Errore nel caricamento degli oratori:", error);
                this.oratoriEstesi = [];
            } finally {
                this.loadingOratori = false;
            }
        }
    },
    watch: {
        '$updaterHub.signals.oratori'() {
            this.ricaricaSilente();
        },
        inlineIdCongregazione(newId) {
            if (newId != null) this.fetchOratori(String(newId));
        },
        '$store.state.idCircoscrizione'(newVal, oldVal) {
            if (newVal && newVal !== oldVal) {
                this._lastFetchedId = undefined;
                this.fetchOratori(this.effectiveIdCongregazione);
            }
        }
    },
    created() {
        this.SUPERADMIN_CODE = SUPERADMIN_CODE;
        this.isAdmin = this.$store.state.oratore.codice == SUPERADMIN_CODE || this.$store.state.oratore.isAdmin;
        bus.$on('dialog-update-oratore', this.onUpdateOratore);
    },
    mounted() {
        if (this.isInline) {
            this.fetchOratori(this.effectiveIdCongregazione);
        }
    },
    beforeDestroy() {
        bus.$off('dialog-update-oratore', this.onUpdateOratore);
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            const targetId = to.params.idCongregazione;
            if (vm._needsRefresh) {
                vm._needsRefresh = false;
                vm._lastFetchedId = targetId;
                vm.fetchOratori(targetId);
                return;
            }
            if (vm.oratoriEstesi.length === 0 || vm._lastFetchedId !== targetId) {
                vm._lastFetchedId = targetId;
                vm.fetchOratori(targetId);
            }
        });
    },
    beforeRouteUpdate(to, from, next) {
        // Navigazione tra congregazioni diverse sulla stessa route
        const targetId = to.params.idCongregazione;
        this._lastFetchedId = targetId;
        this.fetchOratori(targetId);
        next();
    }
}
</script>

<style scoped>
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 4;
}
.cursor-pointer {
    cursor: pointer;
}
.oratore-card {
    cursor: default;
}
.card-nascosta {
    opacity: 0.55;
}
.card-cliccabile {
    cursor: pointer;
    border-left: 3px solid var(--v-primary-base) !important;
    transition: box-shadow 0.15s;
}
.oratore-card:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.13) !important;
}
.contact-link {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: rgba(0,0,0,0.25);
    text-underline-offset: 2px;
}
.schema-badge {
    font-size: 0.68rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 8px;
    background: rgba(var(--v-primary-base), 0.1);
    color: var(--v-primary-base);
    background-color: rgba(25, 118, 210, 0.1);
    color: #1976d2;
}
.theme--dark .schema-badge {
    background-color: rgba(144, 202, 249, 0.15);
    color: #90caf9;
}
</style>
